import { Component, ErrorInfo, ReactNode } from "react";

interface ErrorBoundaryProps {
  children: ReactNode;
}

interface ErrorBoundaryState {
  error: Error | null;
  info: ErrorInfo | null;
}

/**
 * Top-level ErrorBoundary — wraps the entire <Routes/> block so a render
 * crash in any lazy-loaded route presents a friendly fallback instead of
 * blanking the whole app.
 *
 * Reports the error to /api/errors via the existing errorReporter (which
 * batches + dedupes) so admins see a feedback entry under
 * `/admin/feedback` with category=bug + auto_reported=true.
 *
 * Per the project rule: NO class components elsewhere. ErrorBoundary HAS
 * to be a class because React still doesn't expose componentDidCatch on
 * function components (as of React 19.0).
 */
class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { error: null, info: null };
  }

  static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    this.setState({ info });
    // Best-effort report. We import lazily because errorReporter pulls in
    // axios + the api client, and we don't want to inflate the top-level
    // bundle just for a crash path that 99.99% of users never hit.
    import("@/lib/errorReporter")
      .then(({ reportError }) => {
        try {
          // errorReporter.report() adds url/user_agent/fingerprint/breadcrumb
          // internally; we only supply message + stack + severity. We fold the
          // React component stack into `stack` so it still reaches the backend.
          const componentStack = (info?.componentStack || "").slice(0, 2000);
          reportError({
            message: (error?.message || String(error)).slice(0, 1000),
            stack: `${(error?.stack || "").slice(0, 8000)}${
              componentStack ? `\n\nComponent stack:\n${componentStack}` : ""
            }`,
            severity: "error",
          });
        } catch (_) {
          /* ignore */
        }
      })
      .catch(() => {
        /* ignore — error reporter itself crashed */
      });
  }

  handleReload = () => {
    try {
      window.location.reload();
    } catch (_) {
      /* ignore */
    }
  };

  render() {
    if (this.state.error) {
      return (
        <div
          role="alert"
          className="min-h-screen flex flex-col items-center justify-center px-6 text-center"
          style={{ background: "var(--lux-bg, #f5f1ea)", color: "var(--lux-ink, #1a1a1a)" }}
        >
          <h1 className="text-2xl font-semibold mb-3" style={{ fontFamily: "var(--font-display, serif)" }}>
            Something went wrong.
          </h1>
          <p className="max-w-md text-sm opacity-75 mb-6">
            The page hit an unexpected error. We&apos;ve logged it for the team. Try reloading
            — if it keeps happening, contact support and mention the URL above.
          </p>
          <button
            type="button"
            onClick={this.handleReload}
            className="px-5 py-2 rounded-md font-medium"
            style={{ background: "var(--lux-ink, #1a1a1a)", color: "var(--lux-bg, #f5f1ea)" }}
          >
            Reload page
          </button>
          {process.env.NODE_ENV !== "production" && this.state.error?.stack && (
            <pre className="mt-8 text-[10px] text-left opacity-50 max-w-3xl whitespace-pre-wrap overflow-auto max-h-64 p-3 border rounded">
              {String(this.state.error.stack).slice(0, 4000)}
            </pre>
          )}
        </div>
      );
    }
    return this.props.children;
  }
}

export default ErrorBoundary;
